import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Settings, RefreshCw, Sparkles } from 'lucide-react'
import { PromptInput } from './chat/PromptInput'

interface CampaignFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (formData: any) => void
  isLoading: boolean
  currentData?: any
}

export function CampaignFormModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  isLoading, 
  currentData
}: CampaignFormModalProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState(currentData || {})

  const handleGenerateStrategy = () => {
    onSubmit(formData)
    onClose()
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  useEffect(() => {
    if (currentData) {
      setFormData(currentData)
    }
  }, [currentData])

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm z-40"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white dark:bg-gray-950 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="relative flex-shrink-0 px-6 lg:px-8 pt-6 pb-5 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-primary-100 via-white to-white dark:from-primary-900/40 dark:via-gray-950 dark:to-gray-950">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold tracking-[0.18em] text-primary-600 dark:text-primary-400 uppercase flex items-center gap-1.5">
                      <Sparkles className="h-3 w-3" />
                      Campaign
                    </div>
                    <h2 className="text-2xl font-bold mt-1 flex items-center gap-2">
                      <Settings className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      {isEditing ? 'Edit Campaign Settings' : 'Campaign Settings'}
                    </h2>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {isEditing ? 'Modify your campaign parameters and regenerate the strategy.' : 'Review your current campaign configuration.'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isEditing && currentData && (
                      <button
                        onClick={handleEdit}
                        className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-500 transition-colors duration-200 flex items-center"
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                    )}
                    <button
                      onClick={onClose}
                      className="p-2 rounded-full bg-white dark:bg-gray-900/80 border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 shadow-sm"
                      aria-label="Close"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 lg:p-6">
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="text-center mb-6">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Update Your Campaign Strategy
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Modify your campaign parameters and regenerate the strategy
                      </p>
                    </div>
                    <PromptInput 
                      onSubmit={(data) => setFormData(data)} 
                      isLoading={isLoading}
                      initialData={currentData}
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="text-center mb-6">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Current Campaign Settings
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Review your current campaign configuration
                      </p>
                    </div>
                    
                    {currentData && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Campaign Objective</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">{currentData.objective}</p>
                          </div>
                          <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Budget</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">{currentData.budget}</p>
                          </div>
                          <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Duration</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">{currentData.duration}</p>
                          </div>
                          <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Industry</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">{currentData.industry}</p>
                          </div>
                        </div>

                        <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-2">Target Audience</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300">{currentData.targetAudience}</p>
                        </div>

                        <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-2">Channels</h4>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {currentData.channels?.map((channel: string, index: number) => (
                              <span
                                key={index}
                                className="px-2 py-1 text-xs bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-200 rounded-full"
                              >
                                {channel}
                              </span>
                            ))}
                          </div>
                        </div>

                        {currentData.additionalInfo && (
                          <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Additional Information</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">{currentData.additionalInfo}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sticky Footer */}
              {isEditing && (
                <div className="flex-shrink-0 px-6 lg:px-8 py-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={handleCancel}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleGenerateStrategy}
                      disabled={isLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-500 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {isLoading ? 'Generating...' : 'Generate Strategy'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
